import { useEffect, useMemo, useState } from "react";
import { api, getProductionId } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import CustomFieldRenderer from "@/components/CustomFieldRenderer";
import { ClipboardList, Plus, Users2, Wand2, Trash2, Pencil, FileEdit, MessageCircle, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtDate } from "@/lib/format";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

const FIELD_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Paragraph" },
  { value: "number", label: "Number" },
  { value: "yesno", label: "Yes / No" },
  { value: "select", label: "Dropdown" },
  { value: "multiselect", label: "Multi-select" },
  { value: "date", label: "Date" },
  { value: "rating", label: "1–5 Rating" },
];

export default function RehearsalReports({ embedded = false }) {
  const { user } = useAuth();
  const canEdit = ["org_admin", "production_staff", "super_admin"].includes(user?.role);
  const [tab, setTab] = useState("reports");
  const [templates, setTemplates] = useState([]);
  const [reports, setReports] = useState([]);
  const [rehearsals, setRehearsals] = useState([]);
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const productionId = getProductionId();

  const load = async () => {
    const [t, r, rh, u] = await Promise.all([
      api.get("/report-templates", { params: { production_id: productionId } }),
      api.get("/rehearsal-reports", { params: { production_id: productionId } }),
      api.get("/rehearsals", { params: { production_id: productionId } }),
      api.get("/users", { params: { production_id: productionId } }).catch(() => ({ data: [] })),
    ]);
    setTemplates(t.data); setReports(r.data); setRehearsals(rh.data); setUsers(u.data || []);
  };
  useEffect(() => { load(); }, []);

  // When arriving from +New via ?new=1, pop the create dialog automatically.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const p = new URLSearchParams(window.location.search);
    if (embedded && p.get("new") === "1" && templates.length > 0 && canEdit) {
      setCreateOpen(true);
      p.delete("new"); const q = p.toString();
      const url = window.location.pathname + (q ? `?${q}` : "");
      window.history.replaceState({}, "", url);
    }
  }, [templates, embedded, canEdit]);

  return (
    <div className={embedded ? "space-y-4" : "space-y-6"} data-testid="rehearsal-reports-page">
      {!embedded && (
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-gold mb-1">Reports</div>
            <h1 className="font-display text-4xl sm:text-5xl">Rehearsal Reports</h1>
            <p className="text-zinc-500 text-sm mt-2 max-w-xl">Design report templates, capture what happened at each rehearsal, and hand out actionable follow-ups to the team.</p>
          </div>
          {canEdit && (
            <button data-testid="new-report-btn" onClick={() => setCreateOpen(true)} style={goldBtn} className="h-10 px-4 rounded font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50" disabled={templates.length === 0}>
              <Plus className="w-4 h-4" /> New report
            </button>
          )}
        </div>
      )}
      {embedded && canEdit && (
        <div className="flex justify-end">
          <button data-testid="new-report-btn" onClick={() => setCreateOpen(true)} style={goldBtn} className="h-9 px-4 rounded font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50" disabled={templates.length === 0}>
            <Plus className="w-4 h-4" /> New report
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 border-b border-white/10">
        {[
          { k: "reports", label: `Reports (${reports.length})`, icon: FileEdit },
          canEdit ? { k: "templates", label: `Templates (${templates.length})`, icon: Wand2 } : null,
        ].filter(Boolean).map(({ k, label, icon: I }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`report-tab-${k}`}
            className={`px-4 py-2.5 text-sm inline-flex items-center gap-2 border-b-2 transition-colors ${tab === k ? "border-gold text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>
            <I className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "reports" && (
        <ReportsTab reports={reports} rehearsals={rehearsals} onOpen={setDetail} canEdit={canEdit} />
      )}
      {tab === "templates" && (
        <TemplatesTab templates={templates} onChange={load} productionId={productionId} />
      )}

      {createOpen && (
        <CreateReportDialog
          templates={templates}
          rehearsals={rehearsals}
          productionId={productionId}
          onClose={() => setCreateOpen(false)}
          onCreated={async (r) => { setCreateOpen(false); await load(); setDetail(r); }}
        />
      )}

      {detail && (
        <ReportDetailDialog
          report={detail}
          users={users}
          onClose={async () => { setDetail(null); await load(); }}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}

function ReportsTab({ reports, rehearsals, onOpen, canEdit }) {
  const [q, setQ] = useState("");
  const rmap = Object.fromEntries(rehearsals.map((r) => [r.id, r]));
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return reports;
    return reports.filter((r) => (r.title || "").toLowerCase().includes(term) || (r.rehearsal_title || "").toLowerCase().includes(term) || (r.author_name || "").toLowerCase().includes(term));
  }, [reports, q]);

  if (reports.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-white/10 p-10 text-center text-zinc-500">
        <ClipboardList className="w-8 h-8 mx-auto text-zinc-700 mb-3" />
        No reports yet.{canEdit ? " Build a template first, then click New report." : ""}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Input data-testid="reports-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search report, rehearsal, author" className="max-w-md bg-[#0F0F0F] border-white/10 h-10" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((r) => {
          const reh = rmap[r.rehearsal_id];
          return (
            <button key={r.id} onClick={() => onOpen(r)} data-testid={`report-card-${r.id}`}
                    className="text-left rounded-lg border border-white/10 bg-[#0F0F0F] p-5 hover:border-gold/40 transition-colors">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{r.template_name}</div>
              <div className="mt-1 font-display text-lg leading-tight">{r.title || "Untitled"}</div>
              {reh && (
                <div className="mt-2 text-xs text-zinc-400">
                  {reh.title} · {fmtDate(reh.date)}
                </div>
              )}
              <div className="mt-4 flex items-center gap-2 text-[11px]">
                <span className="px-2 py-0.5 rounded-full border border-white/10 text-zinc-400">by {r.author_name}</span>
                {r.actionable_count > 0 && (
                  <span className="px-2 py-0.5 rounded-full border border-gold/30 text-gold inline-flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" /> {r.open_actionable_count}/{r.actionable_count} actionables
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ------------- Templates tab -------------
function TemplatesTab({ templates, onChange, productionId }) {
  const confirmTpl = useConfirm();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [editing, setEditing] = useState(null);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      const { data } = await api.post("/report-templates", form, { params: { production_id: productionId } });
      toast.success(`${data.name} created`);
      setOpen(false); setForm({ name: "", description: "" });
      await onChange();
      setEditing(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };
  const del = async (t) => {
    const ok = await confirmTpl({ title: `Delete "${t.name}"?`, body: "Reports already submitted with this template will stay but detach.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    try {
      await api.delete(`/report-templates/${t.id}`);
      toast.success("Template removed");
      await onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (editing) {
    return <TemplateEditor template={editing} onBack={async () => { await onChange(); setEditing(null); }} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <p className="text-zinc-500 text-sm">Design one or more report templates per production — each is a form the staff fills out after rehearsal.</p>
        <button data-testid="new-template-btn" onClick={() => setOpen(true)} style={goldBtn} className="h-9 px-4 rounded font-semibold text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New template</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates.map((t) => (
          <div key={t.id} className="rounded-lg border border-white/10 bg-[#0F0F0F] p-5 hover:border-white/20 transition-colors group" data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="font-display text-lg">{t.name}</div>
                {t.description && <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{t.description}</p>}
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`edit-template-${t.id}`} onClick={() => setEditing(t)} className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-white/5"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`del-template-${t.id}`} onClick={() => del(t)} className="p-1.5 rounded text-zinc-400 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="mt-3 text-[11px] text-zinc-500">{t.field_count} fields · {t.report_count} reports</div>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="col-span-full text-center py-12 text-zinc-500 text-sm rounded-lg border border-dashed border-white/10">No templates yet. Click New template.</div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">New template</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><Label className="text-zinc-300 text-xs uppercase tracking-widest">Name</Label><Input data-testid="template-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-[#121212] border-white/10 h-11" placeholder="e.g. SM Rehearsal Report" /></div>
            <div><Label className="text-zinc-300 text-xs uppercase tracking-widest">Description</Label><Textarea data-testid="template-desc-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 bg-[#121212] border-white/10" /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="h-9 px-3 rounded border border-white/10 text-sm">Cancel</button>
            <button data-testid="create-template-btn" onClick={create} style={goldBtn} className="h-9 px-4 rounded font-semibold text-sm">Create</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateEditor({ template, onBack }) {
  const confirmField = useConfirm();
  const [t, setT] = useState(null);
  const [meta, setMeta] = useState({ name: "", description: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/report-templates/${template.id}`);
    setT(data);
    setMeta({ name: data.name || "", description: data.description || "" });
  };
  useEffect(() => { load(); }, [template.id]);

  const saveMeta = async () => {
    await api.patch(`/report-templates/${template.id}`, meta);
    toast.success("Saved");
    load();
  };
  const addField = async () => {
    setBusy(true);
    try {
      await api.post(`/report-templates/${template.id}/fields`, {
        label: "Untitled field",
        type: "text",
        required: false,
        options: [],
        position: t.fields.length,
      });
      load();
    } finally { setBusy(false); }
  };
  const saveField = async (f) => {
    await api.patch(`/report-templates/${template.id}/fields/${f.id}`, {
      label: f.label, type: f.type, required: !!f.required,
      options: (f.options_raw ?? (f.options || []).join(", ")).split(",").map((s) => s.trim()).filter(Boolean),
      help_text: f.help_text || "", position: f.position || 0,
    });
    load();
  };
  const delField = async (id) => {
    const ok = await confirmField({ title: "Delete this field?", body: "The field will be removed from this template. Existing report answers are preserved.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    await api.delete(`/report-templates/${template.id}/fields/${id}`);
    load();
  };

  if (!t) return <div className="text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="template-editor">
      <button onClick={onBack} className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1.5"><ArrowRight className="w-4 h-4 rotate-180" /> Back to templates</button>

      <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-5 space-y-3">
        <div><Label className="text-zinc-300 text-xs uppercase tracking-widest">Template name</Label><Input value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} className="mt-2 bg-[#121212] border-white/10 h-11" /></div>
        <div><Label className="text-zinc-300 text-xs uppercase tracking-widest">Description</Label><Textarea rows={2} value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} className="mt-2 bg-[#121212] border-white/10" /></div>
        <div className="flex justify-end"><button onClick={saveMeta} style={goldBtn} className="h-9 px-4 rounded font-semibold text-sm">Save details</button></div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Fields ({t.fields.length})</div>
        <button data-testid="add-field-btn" onClick={addField} disabled={busy} style={goldBtn} className="h-9 px-3 rounded text-sm font-semibold inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add field</button>
      </div>

      <div className="space-y-3">
        {t.fields.map((f, idx) => (
          <FieldEditor key={f.id} f={{ ...f, position: idx }} onSave={saveField} onDelete={() => delField(f.id)} />
        ))}
        {t.fields.length === 0 && <div className="rounded-lg border border-dashed border-white/10 p-8 text-center text-zinc-500 text-sm">Click Add field to get started.</div>}
      </div>
    </div>
  );
}

function FieldEditor({ f, onSave, onDelete }) {
  const [state, setState] = useState({ ...f, options_raw: (f.options || []).join(", ") });
  useEffect(() => { setState({ ...f, options_raw: (f.options || []).join(", ") }); }, [f.id]);
  const needsOptions = ["select", "multiselect"].includes(state.type);
  return (
    <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-4" data-testid={`field-${f.id}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <Input value={state.label} onChange={(e) => setState({ ...state, label: e.target.value })} placeholder="Field label" className="md:col-span-5 bg-[#121212] border-white/10 h-10" />
        <Select value={state.type} onValueChange={(v) => setState({ ...state, type: v })}>
          <SelectTrigger className="md:col-span-3 bg-[#121212] border-white/10 h-10"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10">
            {FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="md:col-span-2 inline-flex items-center gap-2 text-xs text-zinc-300 px-3">
          <input type="checkbox" checked={!!state.required} onChange={(e) => setState({ ...state, required: e.target.checked })} className="accent-gold" /> Required
        </label>
        <button onClick={() => onSave(state)} style={goldBtn} className="md:col-span-1 h-10 rounded font-semibold text-sm">Save</button>
        <button onClick={onDelete} className="md:col-span-1 h-10 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10 inline-flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
      </div>
      {needsOptions && (
        <Input value={state.options_raw} onChange={(e) => setState({ ...state, options_raw: e.target.value })} placeholder="Options (comma separated)" className="mt-2 bg-[#121212] border-white/10 h-9" />
      )}
      <Input value={state.help_text || ""} onChange={(e) => setState({ ...state, help_text: e.target.value })} placeholder="Help text (optional)" className="mt-2 bg-[#121212] border-white/10 h-9" />
    </div>
  );
}

// ------------- Create report dialog -------------
function CreateReportDialog({ templates, rehearsals, productionId, onClose, onCreated }) {
  const [templateId, setTemplateId] = useState(templates[0]?.id || "");
  const [rehearsalId, setRehearsalId] = useState("");
  const [title, setTitle] = useState("");
  const [answers, setAnswers] = useState({});
  const [fields, setFields] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!templateId) return;
    api.get(`/report-templates/${templateId}`).then((r) => setFields(r.data.fields || []));
  }, [templateId]);

  const submit = async () => {
    if (!templateId || !rehearsalId) return toast.error("Pick a template and a rehearsal");
    // required check
    for (const f of fields) {
      if (f.required && (answers[f.id] === undefined || answers[f.id] === "" || (Array.isArray(answers[f.id]) && answers[f.id].length === 0))) {
        return toast.error(`"${f.label}" is required`);
      }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/rehearsal-reports", {
        rehearsal_id: rehearsalId, template_id: templateId, title, answers,
      }, { params: { production_id: productionId } });
      toast.success("Report saved");
      onCreated(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">New rehearsal report</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">Template</Label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger data-testid="new-report-template" className="mt-2 bg-[#121212] border-white/10 h-11"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">Rehearsal</Label>
              <Select value={rehearsalId} onValueChange={setRehearsalId}>
                <SelectTrigger data-testid="new-report-rehearsal" className="mt-2 bg-[#121212] border-white/10 h-11"><SelectValue placeholder="Pick a rehearsal" /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-72">
                  {rehearsals.slice().reverse().map((r) => <SelectItem key={r.id} value={r.id}>{r.title} · {fmtDate(r.date)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-widest">Report title (optional)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Leave blank to auto-generate" className="mt-2 bg-[#121212] border-white/10 h-11" />
          </div>
          {fields.map((f) => (
            <div key={f.id}>
              <CustomFieldRenderer field={f} value={answers[f.id]} onChange={(v) => setAnswers({ ...answers, [f.id]: v })} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="h-9 px-3 rounded border border-white/10 text-sm">Cancel</button>
          <button
            data-testid="submit-report-btn"
            onClick={submit}
            disabled={busy}
            style={goldBtn}
            className="h-9 px-4 rounded font-semibold text-sm inline-flex items-center gap-1.5 hover:brightness-110"
            title="Save the report and notify each assigned actionable owner by email"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
            Submit &amp; notify team
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------- Report detail dialog with actionables -------------
function ReportDetailDialog({ report, users, onClose, canEdit }) {
  const confirmAct = useConfirm();
  const [detail, setDetail] = useState(null);
  const [fields, setFields] = useState([]);
  const [newActionable, setNewActionable] = useState({ description: "", assigned_to_user_id: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/rehearsal-reports/${report.id}`);
    setDetail(data);
    if (data.template_id) {
      const { data: tpl } = await api.get(`/report-templates/${data.template_id}`);
      setFields(tpl.fields || []);
    }
  };
  useEffect(() => { load(); }, [report.id]);

  const addActionable = async () => {
    if (!newActionable.description.trim()) return;
    setBusy(true);
    try {
      await api.post("/actionables", {
        description: newActionable.description,
        assigned_to_user_id: newActionable.assigned_to_user_id || null,
        rehearsal_id: detail.rehearsal_id,
        report_id: detail.id,
      }, { params: { production_id: detail.production_id } });
      toast.success(newActionable.assigned_to_user_id ? "Actionable assigned & notified" : "Actionable added");
      setNewActionable({ description: "", assigned_to_user_id: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };

  const toggleStatus = async (a) => {
    const next = a.status === "done" ? "open" : (a.status === "open" ? "in_progress" : "done");
    await api.patch(`/actionables/${a.id}`, { status: next });
    load();
  };

  const delActionable = async (id) => {
    const ok = await confirmAct({ title: "Delete this actionable?", body: "The follow-up will be removed from the report.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    await api.delete(`/actionables/${id}`);
    load();
  };

  if (!detail) return null;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{detail.title}</DialogTitle>
          <div className="text-xs text-zinc-500 mt-1">
            {detail.template_name} · by {detail.author_name} · {detail.rehearsal ? `${detail.rehearsal.title} · ${fmtDate(detail.rehearsal.date)}` : ""}
          </div>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="space-y-3">
            {fields.map((f) => {
              const v = detail.answers?.[f.id];
              return (
                <div key={f.id} className="rounded border border-white/5 p-3 bg-[#0F0F0F]">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{f.label}</div>
                  <div className="mt-1 text-sm text-zinc-200 whitespace-pre-wrap">
                    {v == null || v === "" ? <span className="text-zinc-600">—</span>
                     : Array.isArray(v) ? v.join(", ")
                     : String(v)}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="pt-2 border-t border-white/10">
            <div className="text-[10px] uppercase tracking-[0.24em] text-gold mb-3">Actionables ({detail.actionables?.length || 0})</div>
            <div className="space-y-2 mb-4">
              {(detail.actionables || []).map((a) => {
                const assignee = users.find((u) => u.id === a.assigned_to_user_id);
                return (
                  <div key={a.id} data-testid={`actionable-${a.id}`} className="rounded border border-white/10 bg-[#0F0F0F] p-3 flex items-start gap-3">
                    <button onClick={() => toggleStatus(a)} className={`shrink-0 mt-0.5 w-4 h-4 rounded-sm border ${a.status === "done" ? "bg-emerald-500/30 border-emerald-500" : a.status === "in_progress" ? "bg-amber-500/30 border-amber-500" : "border-zinc-500"}`} title={a.status}></button>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${a.status === "done" ? "line-through text-zinc-500" : "text-zinc-100"}`}>{a.description}</div>
                      <div className="mt-1 text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
                        {assignee ? <span className="inline-flex items-center gap-1"><Users2 className="w-3 h-3" /> {assignee.name}</span> : <span className="text-zinc-600">Unassigned</span>}
                        <span className="uppercase tracking-widest">· {a.status.replace("_", " ")}</span>
                      </div>
                    </div>
                    {canEdit && (
                      <button onClick={() => delActionable(a.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                );
              })}
              {(detail.actionables || []).length === 0 && <div className="text-xs text-zinc-500 text-center py-3">No actionables yet.</div>}
            </div>
            {canEdit && (
              <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-3 space-y-2">
                <Input data-testid="new-actionable-desc" value={newActionable.description} onChange={(e) => setNewActionable({ ...newActionable, description: e.target.value })} placeholder="What needs to happen next?" className="bg-[#121212] border-white/10 h-10" />
                <div className="flex gap-2">
                  <Select value={newActionable.assigned_to_user_id} onValueChange={(v) => setNewActionable({ ...newActionable, assigned_to_user_id: v })}>
                    <SelectTrigger data-testid="new-actionable-user" className="bg-[#121212] border-white/10 h-10"><SelectValue placeholder="Assign to (optional)" /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-72">
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-zinc-500 text-xs">· {u.role}</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button data-testid="add-actionable-btn" onClick={addActionable} disabled={busy || !newActionable.description.trim()} style={goldBtn} className="h-10 px-4 rounded font-semibold text-sm disabled:opacity-50 shrink-0">Add</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
